import type { ChangeEvent, KeyboardEvent } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { css } from '@emotion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, HStack, Primitive, Text, VStack } from '@rtkwlf/fenrir-react';
import { faPaperclip } from '@rtkwlf/pro-regular-svg-icons/faPaperclip';
import type { ZendeskUploadResponse } from '@rtkwlf/rendall';
import { FileDrop } from 'react-file-drop';
import { useTranslation } from 'react-i18next';

import { FileAttachmentList } from '@packages/components/fileAttachment/FileAttachmentList';
import { DEFAULT_TAB_INDEX, EventKey } from 'ae-constants/constants';

export type UploadFileFunctionType = (input: {
  file: File;
}) => Promise<ZendeskUploadResponse>;

type FileAttachmentProps = {
  setErrorMessage: (error: string | null) => void;
  uploadFile: UploadFileFunctionType;
  deleteFile: (token: string) => void;
  wrapperClass?: string;
  resetFilesCounter?: number;
};

export const MAX_ATTACHMENT_LIMIT = 10;

export const FileAttachment: React.FC<FileAttachmentProps> = ({
  setErrorMessage,
  uploadFile,
  deleteFile,
  wrapperClass,
  resetFilesCounter,
}) => {
  const { t } = useTranslation();
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  useEffect(() => setSelectedFiles([]), [resetFilesCounter]);

  const processFiles = useCallback(
    (files: FileList | null) => {
      const filesArray = Array.from(files ?? []);
      const attachmentArray = [...selectedFiles];

      for (let i = 0; i < filesArray.length; i++) {
        if (attachmentArray.length >= MAX_ATTACHMENT_LIMIT) {
          setErrorMessage(
            t('fileAttachment:errorMessage.maxLimit', {
              maxLimit: MAX_ATTACHMENT_LIMIT,
            }),
          );
          break;
        }
        const isExists = attachmentArray.find(
          (file) => file.name === filesArray[i].name,
        );
        if (!isExists) {
          attachmentArray.push(filesArray[i]);
        }
      }

      setSelectedFiles(attachmentArray);
    },
    [selectedFiles, setSelectedFiles, setErrorMessage, t],
  );

  const onFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    processFiles(event.target.files);
  };

  const onFileDrop = (files: FileList | null) => {
    processFiles(files);
  };

  const handleFileAttachment = useCallback(() => {
    if (hiddenFileInput !== null && hiddenFileInput.current !== null) {
      hiddenFileInput.current.click();
    }
  }, [hiddenFileInput]);

  const handleKeyFileAttachment = useCallback(
    (e: KeyboardEvent): void => {
      if (e.key === EventKey.Return) {
        handleFileAttachment();
      }
    },
    [handleFileAttachment],
  );

  const handleFileDelete = useCallback(
    (fileName: string, token: string): void => {
      const attachmentArray = [...selectedFiles];
      const fileIndex = attachmentArray.findIndex(
        (file) => file.name === fileName,
      );

      if (fileIndex > -1) {
        attachmentArray.splice(fileIndex, 1);
        setSelectedFiles(attachmentArray);
      }

      deleteFile(token);
    },
    [selectedFiles, deleteFile],
  );

  return (
    <Primitive.div className={wrapperClass}>
      {selectedFiles.map((file) => (
        <FileAttachmentList
          key={file.name}
          file={file}
          handleFileDelete={handleFileDelete}
          uploadFile={uploadFile}
        />
      ))}

      <FileDrop onDrop={onFileDrop}>
        <HStack
          backgroundColor="infoHighlight"
          borderStyle="solid"
          borderRadius="default"
          borderWidth="default"
          borderColor="neutralSecondary"
          padding="small"
        >
          <VStack
            backgroundColor="infoHighlight"
            borderRadius="default"
            borderWidth="large"
            borderColor="brandDefault"
            padding="default"
            width="full"
            alignItems="center"
            css={css`
              border-style: dashed;
            `}
          >
            <HStack>
              <Button
                variant="secondary"
                iconLeft={<FontAwesomeIcon icon={faPaperclip} />}
                aria-label={t('fileAttachment:title')}
                onKeyUp={handleKeyFileAttachment}
                tabIndex={DEFAULT_TAB_INDEX}
                onClick={handleFileAttachment}
              >
                {t('fileAttachment:chooseAFile')}
              </Button>

              <input
                onChange={onFileInputChange}
                ref={hiddenFileInput}
                aria-label={t('fileAttachment:chooseAFileText')}
                type="file"
                multiple
                css={css`
                  display: none;
                `}
              />

              <Text styledAs="body3">
                {t('fileAttachment:chooseAFileText')}
              </Text>
            </HStack>
            <Text textColor="brandDefault" styledAs="body3">
              {t('fileAttachment:chooseAFileValidationText')}
            </Text>
          </VStack>
        </HStack>
      </FileDrop>
    </Primitive.div>
  );
};
